// src/components/Header.jsx
import React, { useEffect, useState, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Menu, Transition } from "@headlessui/react";
import { useDispatch, useSelector } from "react-redux";
import { jwtDecode } from "jwt-decode";
import { logout as logoutAction } from "../redux/slices/authSlice";
import SOURCE_MAP from "../config/sourceMap";
import {
  Bell,
  Lock,
  LogOut,
  ChevronDown,
  Wallet as WalletIcon,
  Plus,
  AlertTriangle,
  Info,
  CheckCircle2,
  Megaphone,
  X as Close,
  Search,
  Command,
  Zap,
  TrendingUp,
  Package,
  FileText,
  Settings,
  HelpCircle,
  Sparkles,
  ArrowUpRight,
  Clock,
  Activity,
} from "lucide-react";
import { fetchWalletBalance } from "../redux/slices/walletSlice";

export default function Header({ onMenuClick = () => {}, marquees: propMarquees = [] }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const headerRef = useRef(null);
  const [scrolled, setScrolled] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showCommandPalette, setShowCommandPalette] = useState(false);
  const [showQuickActions, setShowQuickActions] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const searchInputRef = useRef(null);

  // Safe wallet default to avoid undefined runtime errors
  const wallet = useSelector((s) => s.wallet) || {
    balance: null,
    type: null,
    status: "idle",
    lastUpdated: 0,
    error: null,
  };

  const uiMarquees = useSelector((s) => s.ui?.marquees) || [];

  const merchantId = typeof window !== "undefined" ? localStorage.getItem("merchantId") : null;
  const source =
    useSelector((s) => s.auth?.source) || localStorage.getItem("source") || "wayuvega";
  const brand = SOURCE_MAP[source] || SOURCE_MAP.wayuvega;

  // Command Palette - Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Cmd/Ctrl + K for command palette
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setShowCommandPalette(true);
        setTimeout(() => searchInputRef.current?.focus(), 100);
      }
      // Escape to close
      if (e.key === "Escape") {
        setShowCommandPalette(false);
        setShowQuickActions(false);
        setSearchQuery("");
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Quick Actions Menu Items
  const quickActions = [
    { icon: Package, label: "Create Order", path: "/create-forward-order", color: "blue", description: "Start new shipment" },
    { icon: FileText, label: "View Reports", path: "/reports", color: "purple", description: "Analytics & insights" },
    { icon: TrendingUp, label: "Track Shipments", path: "/all-shipments", color: "emerald", description: "Monitor orders" },
    { icon: WalletIcon, label: "Recharge Wallet", path: "/wallet?recharge=1", color: "amber", description: "Add balance" },
  ];

  // Command Palette Items
  const commandItems = useMemo(() => [
    { icon: Package, label: "Create Forward Order", path: "/create-forward-order", shortcut: "C F", category: "Actions" },
    { icon: Package, label: "Create Bulk Order", path: "/create-bulk-order", shortcut: "C B", category: "Actions" },
    { icon: TrendingUp, label: "All Shipments", path: "/all-shipments", shortcut: "A S", category: "Navigation" },
    { icon: FileText, label: "Reports", path: "/reports", shortcut: "R", category: "Navigation" },
    { icon: WalletIcon, label: "Wallet", path: "/wallet", shortcut: "W", category: "Navigation" },
    { icon: Settings, label: "Profile Settings", path: "/profile", shortcut: "P", category: "Settings" },
    { icon: HelpCircle, label: "Help & Support", path: "/support", shortcut: "H", category: "Help" },
  ], []);

  const filteredCommands = useMemo(() => {
    if (!searchQuery.trim()) return commandItems;
    const query = searchQuery.toLowerCase();
    return commandItems.filter(item => 
      item.label.toLowerCase().includes(query) ||
      item.category.toLowerCase().includes(query)
    );
  }, [searchQuery, commandItems]);

  const token = typeof window !== "undefined" ? localStorage.getItem("authToken") : null;
  let username = "USER";
  if (token) {
    try {
      const decoded = jwtDecode(token);
      const raw = decoded.username || decoded.sub || "USER";
      username = raw && raw.length > 0 ? raw.charAt(0).toUpperCase() + raw.slice(1) : "USER";
    } catch {}
  }

  // ===============================
  // --- Token expiry timer ---
  // ===============================
  const [remainingMs, setRemainingMs] = useState(null);
  const expiryRef = useRef(null);
  const timerIntervalRef = useRef(null);

  const setupExpiryFromToken = (jwtToken) => {
    if (!jwtToken) {
      expiryRef.current = null;
      setRemainingMs(null);
      return;
    }
    try {
      const decoded = jwtDecode(jwtToken);
      if (decoded && decoded.exp) {
        expiryRef.current = Number(decoded.exp);
        const msLeft = expiryRef.current * 1000 - Date.now();
        setRemainingMs(msLeft > 0 ? msLeft : 0);
      } else {
        expiryRef.current = null;
        setRemainingMs(null);
      }
    } catch {
      expiryRef.current = null;
      setRemainingMs(null);
    }
  };

  useEffect(() => {
    setupExpiryFromToken(token);

    const startTimer = () => {
      if (timerIntervalRef.current) return;
      timerIntervalRef.current = setInterval(() => {
        if (!expiryRef.current) {
          setRemainingMs(null);
          return;
        }
        const msLeft = expiryRef.current * 1000 - Date.now();
        setRemainingMs(msLeft > 0 ? msLeft : 0);
      }, 1000);
    };

    const stopTimer = () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    };

    if (token) startTimer();
    else stopTimer();

    return () => stopTimer();
  }, [token]);

  // Auto logout when timer reaches zero
  useEffect(() => {
    if (remainingMs == null) return;
    if (remainingMs <= 0) {
      dispatch(logoutAction());
      try {
        localStorage.removeItem("authToken");
        localStorage.removeItem("parentClientId");
        localStorage.removeItem("merchantId");
      } catch {}
      const s = source || localStorage.getItem("source") || "wayuvega";
      const route = (SOURCE_MAP[s] && SOURCE_MAP[s].loginRoute) || "/login";
      navigate(route, { replace: true });
    }
  }, [remainingMs, dispatch, navigate, source]);

  // if token changes in another tab, re-init the expiry
  useEffect(() => {
    const onStorage = (e) => {
      if (!e) return;
      if (e.key === "authToken") {
        setupExpiryFromToken(e.newValue);
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // Format time
  const formatTime = (ms) => {
    if (ms == null) return "—";
    const totalSeconds = Math.max(0, Math.floor(ms / 1000));
    const hrs = Math.floor(totalSeconds / 3600);
    const mins = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;
    if (hrs > 0) return `${String(hrs).padStart(2, "0")}:${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
    return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
  };

  const percentLeft = (() => {
    if (!expiryRef.current || remainingMs == null) return 0;
    try {
      const decoded = jwtDecode(token || "");
      const iat = decoded?.iat ? Number(decoded.iat) : null;
      const exp = decoded?.exp ? Number(decoded.exp) : null;
      if (exp && iat) {
        const total = (exp - iat) * 1000;
        return Math.max(0, Math.min(100, (remainingMs / total) * 100));
      }
    } catch {}
    return Math.max(0, Math.min(100, (remainingMs / (60 * 60 * 1000)) * 100));
  })();

  const isCritical = remainingMs != null && remainingMs <= 60_000;
  const showTimer = remainingMs != null && expiryRef.current != null;

  // Wallet polling + focus/visibility effects
  useEffect(() => {
    if (token && merchantId) dispatch(fetchWalletBalance());
  }, [dispatch, token, merchantId]);

  useEffect(() => {
    const STALE_MS = 60_000;
    const onFocusOrVisible = () => {
      const last = wallet?.lastUpdated ?? 0;
      if (Date.now() - last > STALE_MS) dispatch(fetchWalletBalance());
    };
    const onFocus = () => onFocusOrVisible();
    const onVisibility = () => {
      if (document.visibilityState === "visible") onFocusOrVisible();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [dispatch, wallet?.lastUpdated]);

  useEffect(() => {
    let id;
    const start = () => {
      if (typeof document !== "undefined" && document.visibilityState === "visible") {
        id = setInterval(() => dispatch(fetchWalletBalance()), 90_000);
      }
    };
    const stop = () => id && clearInterval(id);
    start();
    const onVisibility = () => {
      stop();
      start();
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [dispatch]);

  // Cross-tab sync
  useEffect(() => {
    const bc = new BroadcastChannel("wallet");
    bc.onmessage = (e) => {
      if (e?.data === "refresh-balance") dispatch(fetchWalletBalance());
    };
    return () => bc.close();
  }, [dispatch]);

  // Header chrome
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    const handleClickOutside = (e) => {
      if (showNotifications && !e.target.closest(".notification-container")) {
        setShowNotifications(false);
      }
      if (showQuickActions && !e.target.closest(".quick-actions-container")) {
        setShowQuickActions(false);
      }
    };
    window.addEventListener("scroll", handleScroll);
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showNotifications, showQuickActions]);

  useEffect(() => {
    const onStorage = (e) => {
      if (!e) return;
      if (e.key === "authToken" && e.oldValue && !e.newValue) {
        dispatch(logoutAction());
        const s = localStorage.getItem("source") || "wayuvega";
        const route = (SOURCE_MAP[s] && SOURCE_MAP[s].loginRoute) || "/login";
        navigate(route, { replace: true });
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [dispatch, navigate]);

  const handleLogout = () => {
    dispatch(logoutAction());
    try {
      localStorage.removeItem("authToken");
      localStorage.removeItem("parentClientId");
      localStorage.removeItem("merchantId");
    } catch {}
    const s = source || localStorage.getItem("source") || "wayuvega";
    const route = (SOURCE_MAP[s] && SOURCE_MAP[s].loginRoute) || "/login";
    navigate(route, { replace: true });
  };

  const formatINR = new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  });
  const balance = wallet?.balance;
  const walletVariant =
    balance == null ? "idle" : balance < 200 ? "low" : balance <= 1000 ? "medium" : "high";

  const pillBase =
    "wallet-pill group relative inline-flex items-center gap-2 pl-2 pr-2 py-1.5 rounded-xl border transition-all hover:scale-[1.02] hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-400 cursor-pointer";
  
  const variantWrap = {
    idle: "bg-gradient-to-br from-gray-50 to-gray-100 border-gray-200 text-gray-700",
    high: "bg-gradient-to-br from-emerald-50 to-white border-emerald-200 text-gray-900 shadow-sm",
    medium: "bg-gradient-to-br from-amber-50 to-white border-amber-300 text-amber-900 shadow-sm",
    low: "bg-gradient-to-br from-rose-50 to-white border-rose-300 text-rose-800 shadow-sm",
  };
  
  const variantDot = {
    idle: "bg-gray-400",
    high: "bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.6)]",
    medium: "bg-amber-500 shadow-[0_0_8px_rgba(245,158,11,0.6)]",
    low: "bg-rose-500 shadow-[0_0_8px_rgba(239,68,68,0.6)] animate-soft-blink",
  };

  const MARQUEE_VARIANTS = {
    critical: {
      bg: "linear-gradient(135deg, #fee2e2, #fecaca, #fee2e2)",
      border: "rgba(225,29,72,.3)",
      color: "#7f1d1d",
      icon: <AlertTriangle className="w-3.5 h-3.5" />,
    },
    warning: {
      bg: "linear-gradient(135deg, #fff7ed, #ffedd5, #fff7ed)",
      border: "rgba(234,88,12,.3)",
      color: "#7c2d12",
      icon: <AlertTriangle className="w-3.5 h-3.5" />,
    },
    info: {
      bg: "linear-gradient(135deg, #eff6ff, #dbeafe, #eff6ff)",
      border: "rgba(37,99,235,.3)",
      color: "#1e3a8a",
      icon: <Info className="w-3.5 h-3.5" />,
    },
    success: {
      bg: "linear-gradient(135deg, #ecfdf5, #d1fae5, #ecfdf5)",
      border: "rgba(16,185,129,.3)",
      color: "#065f46",
      icon: <CheckCircle2 className="w-3.5 h-3.5" />,
    },
    brand: {
      bg: "linear-gradient(135deg, #eef2ff, #e0e7ff, #eef2ff)",
      border: "rgba(99,102,241,.3)",
      color: "#3730a3",
      icon: <Megaphone className="w-3.5 h-3.5" />,
    },
  };

  const combinedExternal = useMemo(() => {
    const incoming = [
      ...(Array.isArray(propMarquees) ? propMarquees : []),
      ...(Array.isArray(uiMarquees) ? uiMarquees : []),
    ];
    const map = new Map();
    incoming.forEach((m) => {
      if (m && m.id) map.set(m.id, m);
    });
    return Array.from(map.values());
  }, [propMarquees, uiMarquees]);

  const systemMarquees = useMemo(() => {
    const rows = [];
    if (walletVariant === "low") {
      rows.push({
        id: "wallet-low",
        text: "⚡ Low wallet balance — recharge to keep your orders flowing smoothly",
        variant: "critical",
        direction: "rtl",
        cta: { label: "Recharge", to: "/wallet?recharge=1" },
        dismissible: true,
      });
    }
    return rows;
  }, [walletVariant]);

  const allMarquees = [...systemMarquees, ...combinedExternal];

  const [dismissed, setDismissed] = useState(() => {
    try {
      const raw = sessionStorage.getItem("dismissedMarquees");
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  });

  const handleDismiss = (id) => {
    setDismissed((prev) => {
      const next = { ...prev, [id]: true };
      try {
        sessionStorage.setItem("dismissedMarquees", JSON.stringify(next));
      } catch {}
      return next;
    });
  };

  const visibleMarquees = allMarquees.filter((m) => !dismissed[m.id]);

  const BAR_HEIGHT = 18;
  const BAR_GAP = 4;
  const totalBannerHeight =
    visibleMarquees.length > 0 ? visibleMarquees.length * BAR_HEIGHT + (visibleMarquees.length - 1) * BAR_GAP : 0;

  return (
    <>
      <style>{`
        /* ========================= ADVANCED ANIMATIONS ========================= */
        @keyframes shimmer { 
          0% { background-position: -200% 0; } 
          100% { background-position: 200% 0; } 
        }
        
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-4px); }
        }
        
        @keyframes glow {
          0%, 100% { box-shadow: 0 0 5px rgba(99,102,241,0.5); }
          50% { box-shadow: 0 0 20px rgba(99,102,241,0.8), 0 0 30px rgba(99,102,241,0.4); }
        }
        
        @keyframes slideInFromTop {
          0% { transform: translateY(-100%); opacity: 0; }
          100% { transform: translateY(0); opacity: 1; }
        }
        
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes scaleIn {
          from { transform: scale(0.95); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
        
        @keyframes ripple {
          0% { transform: scale(0); opacity: 1; }
          100% { transform: scale(4); opacity: 0; }
        }

        /* ========================= SHIMMER & EFFECTS ========================= */
        .shimmer-effect { 
          position: relative; 
          overflow: hidden; 
        }
        .shimmer-effect::before {
          content: ''; 
          position: absolute; 
          inset: 0;
          background: linear-gradient(90deg, transparent 0%, rgba(255,255,255,.6) 50%, transparent 100%);
          background-size: 200% 100%; 
          animation: shimmer 3s ease-in-out infinite; 
          pointer-events: none; 
          border-radius: inherit;
        }

        .glass-morphism { 
          background: rgba(255,255,255,.92); 
          backdrop-filter: blur(20px) saturate(180%); 
          -webkit-backdrop-filter: blur(20px) saturate(180%); 
        }
        
        .header-border { 
          border-bottom: 1px solid rgba(0,0,0,.08); 
          box-shadow: 0 1px 3px rgba(0,0,0,.04), 0 1px 2px rgba(0,0,0,.02);
        }

        .notification-panel { 
          animation: slideDown .3s cubic-bezier(0.16, 1, 0.3, 1); 
        }
        
        @keyframes slideDown { 
          from { opacity:0; transform: translateY(-20px);} 
          to { opacity:1; transform: translateY(0);} 
        }

        .avatar-ring { 
          box-shadow: 0 0 0 2px #fff, 0 0 0 4px rgba(99,102,241,.15),
                      0 4px 6px rgba(99,102,241,.1); 
        }
        
        .hover-lift { 
          transition: transform .25s cubic-bezier(0.16, 1, 0.3, 1), 
                      box-shadow .25s cubic-bezier(0.16, 1, 0.3, 1); 
        }
        .hover-lift:hover { 
          transform: translateY(-2px); 
          box-shadow: 0 8px 16px rgba(0,0,0,.12), 0 4px 8px rgba(0,0,0,.08); 
        }

        /* ========================= WALLET ENHANCEMENTS ========================= */
        @keyframes soft-blink { 
          0%,100% { opacity:1; transform: scale(1);} 
          50% { opacity:.7; transform: scale(.95);} 
        }
        .animate-soft-blink { 
          animation: soft-blink 1.5s ease-in-out infinite; 
        }
        
        .wallet-tooltip { 
          opacity:0; 
          transform: translateY(-8px) translateX(-50%); 
          transition: all .2s cubic-bezier(0.16, 1, 0.3, 1); 
          pointer-events:none; 
        }
        .group:hover .wallet-tooltip, .group:focus-within .wallet-tooltip { 
          opacity:1; 
          transform: translateY(0) translateX(-50%); 
        }
        
        @keyframes subtle-bounce { 
          0%,100% { transform: translateY(0) rotate(0deg);} 
          50% { transform: translateY(-2px) rotate(5deg);} 
        }
        .plus-bounce:hover { 
          animation: subtle-bounce .6s ease; 
        }

        /* ========================= COMMAND PALETTE ========================= */
        .command-palette-backdrop {
          position: fixed;
          inset: 0;
          background: rgba(15, 23, 42, 0.6);
          backdrop-filter: blur(4px);
          z-index: 9999;
          animation: fadeIn .2s ease;
        }
        
        .command-palette {
          position: fixed;
          top: 15%;
          left: 50%;
          transform: translateX(-50%);
          width: 90%;
          max-width: 640px;
          background: white;
          border-radius: 16px;
          box-shadow: 0 25px 50px -12px rgba(0,0,0,.25), 
                      0 0 0 1px rgba(0,0,0,.05);
          z-index: 10000;
          animation: scaleIn .25s cubic-bezier(0.16, 1, 0.3, 1);
          overflow: hidden;
        }
        
        .command-search {
          width: 100%;
          padding: 20px 24px;
          font-size: 16px;
          border: none;
          outline: none;
          background: transparent;
          font-weight: 500;
        }
        
        .command-list {
          max-height: 400px;
          overflow-y: auto;
          padding: 8px;
        }
        
        .command-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 16px;
          border-radius: 8px;
          cursor: pointer;
          transition: all .15s ease;
          font-size: 14px;
          font-weight: 500;
        }
        
        .command-item:hover {
          background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
          transform: translateX(4px);
        }
        
        .command-category {
          padding: 12px 16px 8px;
          font-size: 11px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 1px;
          color: #64748b;
        }

        /* ========================= QUICK ACTIONS ========================= */
        .quick-actions-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 12px;
          padding: 8px;
        }
        
        .quick-action-card {
          position: relative;
          padding: 16px;
          border-radius: 12px;
          cursor: pointer;
          transition: all .25s cubic-bezier(0.16, 1, 0.3, 1);
          overflow: hidden;
        }
        
        .quick-action-card::before {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(135deg, rgba(255,255,255,.9), rgba(255,255,255,.6));
          transition: opacity .25s ease;
        }
        
        .quick-action-card:hover {
          transform: translateY(-4px) scale(1.02);
          box-shadow: 0 12px 24px rgba(0,0,0,.15);
        }
        
        .quick-action-card:hover::before {
          opacity: .8;
        }

        /* ========================= MARQUEE ========================= */
        .inside-marquee {
          position: absolute;
          left: 8px;
          right: 8px;
          bottom: 4px;
          pointer-events: auto;
        }
        
        .inside-marquee-item {
          width: 100%;
          height: 18px;
          border-radius: 999px;
          overflow: hidden;
          box-shadow: 0 2px 8px rgba(0,0,0,.08), 0 1px 4px rgba(0,0,0,.04);
          display: flex;
          align-items: center;
          padding-left: 10px;
          padding-right: 10px;
          backdrop-filter: blur(10px);
        }
        
        .inside-marquee-track {
          position: relative;
          white-space: nowrap;
          display: inline-flex;
          gap: 48px;
          will-change: transform;
        }
        
        .inside-marquee-text {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          font-weight: 700;
          font-size: 11px;
          letter-spacing: .3px;
        }
        
        .inside-marquee-cta {
          margin-left: auto;
          flex-shrink: 0;
          padding: 3px 10px;
          font-size: 10px;
          font-weight: 700;
          border-radius: 999px;
          cursor: pointer;
          transition: all .2s cubic-bezier(0.16, 1, 0.3, 1);
          backdrop-filter: blur(10px);
        }
        
        .inside-marquee-cta:hover {
          transform: scale(1.08) translateY(-1px);
          box-shadow: 0 4px 12px rgba(0,0,0,.15);
        }
        
        .inside-marquee-dismiss {
          flex-shrink: 0;
          margin-left: 8px;
          padding: 2px;
          border-radius: 50%;
          cursor: pointer;
          transition: all .2s ease;
        }
        
        .inside-marquee-dismiss:hover {
          background: rgba(0,0,0,.1);
          transform: rotate(90deg);
        }

        @keyframes marquee-ltr {
          0%   { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        
        @keyframes marquee-rtl {
          0%   { transform: translateX(100%); }
          100% { transform: translateX(-100%); }
        }

        /* ========================= TOKEN TIMER ========================= */
        .token-timer { 
          display: none; 
        }
        
        @media (min-width: 768px) {
          .token-timer { 
            display: flex; 
            align-items: center; 
            pointer-events: auto; 
            margin-left: 0; 
          }
        }

        .token-timer-inner {
          position: relative;
          display: inline-flex;
          align-items: center;
          gap: 10px;
          min-width: 96px;
          height: 38px;
          padding: 8px 12px;
          border-radius: 14px;
          background: linear-gradient(135deg, rgba(255,255,255,0.98), rgba(248,250,252,0.98));
          border: 1.5px solid rgba(100,116,139,0.1);
          box-shadow: 0 4px 12px rgba(15,23,42,0.06), 
                      0 2px 4px rgba(15,23,42,0.04);
          color: #0f172a;
          transition: all .25s cubic-bezier(0.16, 1, 0.3, 1);
        }
        
        .token-timer-inner:hover { 
          transform: translateY(-2px); 
          box-shadow: 0 12px 24px rgba(15,23,42,0.1), 
                      0 4px 8px rgba(15,23,42,0.06); 
          border-color: rgba(99,102,241,0.2);
        }

        .timer-ring {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 24px;
          height: 24px;
          flex-shrink: 0;
        }
        
        .timer-ring svg { 
          transform: rotate(-90deg); 
          width: 24px; 
          height: 24px; 
        }
        
        .timer-ring-bg { 
          stroke: rgba(15,23,42,0.08); 
          stroke-width: 3; 
          fill: none; 
        }
        
        .timer-ring-fg { 
          stroke: #3b82f6; 
          stroke-width: 3.5; 
          stroke-linecap: round; 
          fill: none; 
          transition: stroke 200ms ease, stroke-dashoffset 300ms linear; 
          filter: drop-shadow(0 0 2px rgba(59,130,246,0.4));
        }

        .timer-ring-critical .timer-ring-fg { 
          stroke: #ef4444; 
          filter: drop-shadow(0 0 3px rgba(239,68,68,0.5));
          animation: pulse-glow 1s ease-in-out infinite;
        }
        
        @keyframes pulse-glow {
          0%, 100% { filter: drop-shadow(0 0 3px rgba(239,68,68,0.5)); }
          50% { filter: drop-shadow(0 0 6px rgba(239,68,68,0.8)); }
        }
        
        .timer-critical {
          border-color: rgba(239,68,68,0.2);
          background: linear-gradient(135deg, rgba(254,242,242,0.98), rgba(254,226,226,0.98));
          box-shadow: 0 8px 20px rgba(239,68,68,0.12), 
                      0 4px 8px rgba(239,68,68,0.08);
          color: #991b1b;
        }

        .timer-text {
          font-weight: 700;
          font-size: 13.5px;
          font-variant-numeric: tabular-nums;
          letter-spacing: 0.3px;
          white-space: nowrap;
        }

        .timer-tooltip {
          position: absolute;
          top: calc(100% + 8px);
          left: 50%;
          transform: translateX(-50%) translateY(6px);
          white-space: nowrap;
          padding: 8px 12px;
          font-size: 12px;
          font-weight: 600;
          border-radius: 8px;
          background: linear-gradient(135deg, rgba(17,24,39,0.97), rgba(30,41,59,0.97));
          color: #fff;
          box-shadow: 0 8px 24px rgba(2,6,23,0.5);
          opacity: 0;
          pointer-events: none;
          transition: opacity .2s ease, transform .2s cubic-bezier(0.16, 1, 0.3, 1);
          z-index: 60;
        }
        
        .timer-tooltip::before {
          content: '';
          position: absolute;
          bottom: 100%;
          left: 50%;
          transform: translateX(-50%);
          border: 6px solid transparent;
          border-bottom-color: rgba(17,24,39,0.97);
        }
        
        .token-timer-inner:hover .timer-tooltip,
        .token-timer-inner:focus-within .timer-tooltip {
          opacity: 1;
          pointer-events: auto;
          transform: translateX(-50%) translateY(0);
        }

        .timer-accessible {
          position: absolute;
          width: 1px;
          height: 1px;
          padding: 0;
          margin: -1px;
          overflow: hidden;
          clip: rect(0,0,0,0);
          white-space: nowrap;
          border: 0;
        }

        /* ========================= RESPONSIVE ========================= */
        @media (prefers-reduced-motion: reduce) {
          .inside-marquee-track { animation: none !important; transform: translateX(0) !important; }
          .animate-soft-blink { animation: none; }
          * { animation-duration: 0.01ms !important; transition-duration: 0.01ms !important; }
        }

        /* ========================= SCROLLBAR ========================= */
        .command-list::-webkit-scrollbar {
          width: 8px;
        }
        
        .command-list::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 10px;
        }
        
        .command-list::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 10px;
        }
        
        .command-list::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }

        /* ========================= FOCUS STYLES ========================= */
        .focus-ring:focus-visible {
          outline: 2px solid #3b82f6;
          outline-offset: 2px;
        }

        /* ========================= BADGE ========================= */
        .notification-badge {
          position: absolute;
          top: -2px;
          right: -2px;
          width: 18px;
          height: 18px;
          background: linear-gradient(135deg, #ef4444, #dc2626);
          color: white;
          font-size: 10px;
          font-weight: 700;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 2px solid white;
          box-shadow: 0 2px 8px rgba(239,68,68,0.4);
          animation: scaleIn .3s cubic-bezier(0.16, 1, 0.3, 1);
        }
      `}</style>

      {/* Command Palette */}
      <Transition show={showCommandPalette} as={React.Fragment}>
        <div className="command-palette-backdrop" onClick={() => setShowCommandPalette(false)}>
          <div className="command-palette" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 px-6 py-5 border-b border-gray-100">
              <Command className="w-5 h-5 text-gray-400" />
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search for commands, pages, or actions..."
                className="command-search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <kbd className="px-2 py-1 text-xs font-semibold text-gray-500 bg-gray-100 border border-gray-200 rounded">ESC</kbd>
            </div>
            
            <div className="command-list">
              {filteredCommands.length === 0 ? (
                <div className="py-12 text-center">
                  <Search className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-sm font-semibold text-gray-500">No results found</p>
                  <p className="text-xs text-gray-400 mt-1">Try searching for something else</p>
                </div>
              ) : (
                (() => {
                  const grouped = filteredCommands.reduce((acc, item) => {
                    if (!acc[item.category]) acc[item.category] = [];
                    acc[item.category].push(item);
                    return acc;
                  }, {});
                  
                  return Object.entries(grouped).map(([category, items]) => (
                    <div key={category}>
                      <div className="command-category">{category}</div>
                      {items.map((item, idx) => (
                        <div
                          key={idx}
                          className="command-item"
                          onClick={() => {
                            navigate(item.path);
                            setShowCommandPalette(false);
                            setSearchQuery("");
                          }}
                        >
                          <item.icon className="w-4 h-4 text-gray-400" />
                          <span className="flex-1">{item.label}</span>
                          {item.shortcut && (
                            <kbd className="px-2 py-1 text-xs font-semibold text-gray-500 bg-gray-100 border border-gray-200 rounded">
                              {item.shortcut}
                            </kbd>
                          )}
                        </div>
                      ))}
                    </div>
                  ));
                })()
              )}
            </div>
            
            <div className="flex items-center justify-between px-6 py-3 border-t border-gray-100 bg-gray-50">
              <div className="flex items-center gap-4 text-xs text-gray-500">
                <div className="flex items-center gap-1">
                  <kbd className="px-1.5 py-0.5 font-semibold bg-white border border-gray-200 rounded">↑</kbd>
                  <kbd className="px-1.5 py-0.5 font-semibold bg-white border border-gray-200 rounded">↓</kbd>
                  <span>Navigate</span>
                </div>
                <div className="flex items-center gap-1">
                  <kbd className="px-1.5 py-0.5 font-semibold bg-white border border-gray-200 rounded">↵</kbd>
                  <span>Select</span>
                </div>
              </div>
              <div className="text-xs text-gray-400 font-medium">
                <span className="text-indigo-600 font-semibold">⌘K</span> to open
              </div>
            </div>
          </div>
        </div>
      </Transition>

      <header
        ref={headerRef}
        className={`fixed top-0 left-0 right-0 z-50 h-16 transition-all duration-500 ease-out ${
          scrolled ? "glass-morphism header-border shadow-lg" : "bg-white border-b border-gray-100"
        }`}
      >
        <div
          className="h-full px-4 sm:px-6 lg:px-8 flex items-center justify-between relative"
          style={{ paddingBottom: totalBannerHeight > 0 ? totalBannerHeight + 6 : 0 }}
        >
          {/* Left Section - Logo */}
          <div className="flex items-center gap-3 min-w-0">
            <button
              type="button"
              onClick={onMenuClick}
              className="md:hidden p-2.5 rounded-xl hover:bg-gradient-to-br hover:from-indigo-50 hover:to-blue-50 active:scale-95 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:ring-offset-1"
              aria-label="Open menu"
            >
              <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>

            <div className="flex items-center gap-3 min-w-0">
              <div className="relative shimmer-effect rounded-xl p-1.5 hover:scale-105 transition-transform duration-300">
                <img src={brand.logo} alt={`${brand.title} logo`} className="h-9 w-auto flex-shrink-0" />
              </div>

              <div className="hidden sm:flex flex-col justify-center min-w-0">
                <span
                  className={`font-extrabold text-lg leading-tight tracking-tight truncate ${
                    brand.textColor || "bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent"
                  }`}
                >
                  {brand.title}
                </span>
                {brand.tagline && (
                  <span className="text-xs text-gray-500 font-semibold leading-tight truncate flex items-center gap-1">
                    {brand.tagline}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Right Section - Actions */}
          <div className="flex items-center gap-3 flex-shrink-0">
            {/* Command Palette Trigger - Desktop */}
            <button
              type="button"
              onClick={() => setShowCommandPalette(true)}
              className="hidden lg:flex items-center gap-2 px-3 py-1.5 rounded-xl bg-gray-50 hover:bg-gray-100 border border-gray-200 hover:border-gray-300 transition-all duration-200 group"
              title="Search (⌘K)"
            >
              <Search className="w-4 h-4 text-gray-400 group-hover:text-gray-600 transition-colors" />
              <span className="text-xs text-gray-500 font-medium">Search</span>
              <kbd className="px-1.5 py-0.5 text-xs font-semibold text-gray-500 bg-white border border-gray-200 rounded">⌘K</kbd>
            </button>

            {/* Quick Actions - Mobile */}
            <button
              type="button"
              onClick={() => setShowQuickActions(!showQuickActions)}
              className="lg:hidden p-2.5 rounded-xl hover:bg-gradient-to-br hover:from-indigo-50 hover:to-blue-50 active:scale-95 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:ring-offset-1 relative hover-lift quick-actions-container"
              aria-label="Quick actions"
            >
              <Zap className="w-5 h-5 text-gray-600" strokeWidth={2} />
              
              <Transition
                show={showQuickActions}
                as={React.Fragment}
                enter="transition ease-out duration-200"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-150"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <div className="absolute right-0 mt-3 w-80 origin-top-right rounded-2xl bg-white shadow-2xl ring-1 ring-black ring-opacity-5 focus:outline-none notification-panel overflow-hidden">
                  <div className="px-5 py-3.5 border-b border-gray-100 bg-gradient-to-r from-indigo-50 to-blue-50">
                    <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2">
                      <Zap className="w-4 h-4 text-indigo-600" />
                      Quick Actions
                    </h3>
                  </div>
                  
                  <div className="quick-actions-grid p-4">
                    {quickActions.map((action, idx) => (
                      <div
                        key={idx}
                        className="quick-action-card border-2 border-gray-100"
                        style={{
                          background: `linear-gradient(135deg, 
                            ${action.color === 'blue' ? '#dbeafe' : 
                              action.color === 'purple' ? '#f3e8ff' : 
                              action.color === 'emerald' ? '#d1fae5' : 
                              '#fef3c7'} 0%, 
                            white 100%)`
                        }}
                        onClick={() => {
                          navigate(action.path);
                          setShowQuickActions(false);
                        }}
                      >
                        <div className="relative z-10">
                          <action.icon className={`w-6 h-6 mb-2 ${
                            action.color === 'blue' ? 'text-blue-600' :
                            action.color === 'purple' ? 'text-purple-600' :
                            action.color === 'emerald' ? 'text-emerald-600' :
                            'text-amber-600'
                          }`} />
                          <h4 className="font-bold text-sm text-gray-900 mb-1">{action.label}</h4>
                          <p className="text-xs text-gray-600">{action.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </Transition>
            </button>

            {/* Wallet */}
            <div className="hidden md:flex items-center gap-2">
              <button
                type="button"
                onClick={() => navigate("/wallet")}
                className={`${pillBase} ${variantWrap[walletVariant]}`}
                title="Go to Wallet"
                aria-label={`Wallet balance ${balance == null ? "not available" : formatINR.format(balance)}`}
              >
                <span className={`w-2.5 h-2.5 rounded-full ${variantDot[walletVariant]}`} />
                <WalletIcon className="w-4.5 h-4.5 opacity-80" />
                <span className="text-xs text-gray-500 font-semibold">Wallet</span>
                <span
                  className={
                    "text-sm font-bold tabular-nums " +
                    (balance == null
                      ? "text-gray-500"
                      : balance < 200
                        ? "text-rose-700"
                        : balance <= 1000
                          ? "text-amber-800"
                          : "text-gray-900")
                  }
                >
                  {balance == null ? "—" : formatINR.format(balance)}
                </span>

                <div className="wallet-tooltip absolute -bottom-12 left-1/2 whitespace-nowrap rounded-lg bg-gradient-to-r from-gray-900 to-gray-800 px-3 py-2 text-xs font-semibold text-white shadow-2xl">
                  View wallet details
                  <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-gray-900 rotate-45"></div>
                </div>
              </button>

              <button
                type="button"
                onClick={() => navigate("/wallet?recharge=1")}
                className={`plus-bounce inline-flex items-center justify-center w-9 h-9 rounded-xl border-2 transition-all duration-200 
                  ${
                    walletVariant === "high"
                      ? "bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-300 text-emerald-700 hover:from-emerald-100 hover:to-emerald-200 hover:shadow-lg hover:shadow-emerald-200/50"
                      : walletVariant === "medium"
                        ? "bg-gradient-to-br from-amber-50 to-amber-100 border-amber-300 text-amber-700 hover:from-amber-100 hover:to-amber-200 hover:shadow-lg hover:shadow-amber-200/50"
                        : walletVariant === "low"
                          ? "bg-gradient-to-br from-rose-50 to-rose-100 border-rose-300 text-rose-700 hover:from-rose-100 hover:to-rose-200 hover:shadow-lg hover:shadow-rose-200/50"
                          : "bg-gradient-to-br from-gray-50 to-gray-100 border-gray-300 text-gray-700 hover:from-gray-100 hover:to-gray-200"
                  }`}
                title="Quick recharge"
                aria-label="Quick recharge"
              >
                <Plus className="w-5 h-5" strokeWidth={2.5} />
              </button>
            </div>

            {/* Token Timer */}
            {showTimer && (
              <div className="token-timer" role="status" aria-live="polite" aria-atomic="true">
                <div
                  className={`token-timer-inner ${isCritical ? "timer-critical" : ""}`}
                  title={isCritical ? "Token expiring soon — you will be logged out when the timer reaches zero" : "Session expiry timer"}
                  tabIndex={0}
                  aria-label={remainingMs != null ? `Session expires in ${formatTime(remainingMs)}` : "Session expiry not available"}
                >
                  <div className={`timer-ring ${isCritical ? "timer-ring-critical" : ""}`} aria-hidden="true">
                    <svg viewBox="0 0 36 36" aria-hidden="true" focusable="false" role="img">
                      <circle className="timer-ring-bg" cx="18" cy="18" r="8" />
                      <circle
                        className="timer-ring-fg"
                        cx="18"
                        cy="18"
                        r="8"
                        strokeDasharray={Math.PI * 2 * 8}
                        strokeDashoffset={(1 - percentLeft / 100) * Math.PI * 2 * 8}
                      />
                    </svg>
                  </div>

                  <div className="timer-text" style={{ color: isCritical ? "#991b1b" : "#0f172a" }}>
                    {formatTime(remainingMs)}
                  </div>

                  <div className="timer-tooltip" role="tooltip">
                    Session expires at zero
                  </div>

                  <div className="timer-accessible" aria-hidden="false">
                    {remainingMs != null ? `Session expires in ${formatTime(remainingMs)}` : `Session expiry not available`}
                  </div>
                </div>
              </div>
            )}

            {/* Notifications */}
            <div className="hidden sm:block relative notification-container">
              <button
                onClick={() => setShowNotifications(!showNotifications)}
                className="relative p-2.5 rounded-xl hover:bg-gradient-to-br hover:from-indigo-50 hover:to-blue-50 active:scale-95 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:ring-offset-1 hover-lift"
                aria-label="Notifications"
              >
                <Bell className="w-5 h-5 text-gray-600" strokeWidth={2} />
                {/* Notification badge - uncomment when needed */}
                {/* <span className="notification-badge">3</span> */}
              </button>

              <Transition
                show={showNotifications}
                as={React.Fragment}
                enter="transition ease-out duration-200"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-150"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <div className="absolute right-0 mt-3 w-96 origin-top-right rounded-2xl bg-white shadow-2xl ring-1 ring-black ring-opacity-5 focus:outline-none notification-panel overflow-hidden">
                  <div className="px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-indigo-50 via-blue-50 to-indigo-50">
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2">
                        <Bell className="w-4 h-4" />
                        Notifications
                      </h3>
                      <span className="text-xs font-semibold text-indigo-600 px-2 py-1 bg-indigo-100 rounded-full">
                        New
                      </span>
                    </div>
                  </div>

                  <div className="px-6 py-16 text-center">
                    <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-indigo-50 via-blue-50 to-indigo-50 mb-4 relative">
                      <Bell className="w-9 h-9 text-indigo-500" strokeWidth={2} />
                      <Sparkles className="w-4 h-4 text-yellow-500 absolute -top-1 -right-1" />
                    </div>
                    <p className="text-sm font-bold text-gray-900 mb-2">All caught up!</p>
                    <p className="text-xs text-gray-500 leading-relaxed max-w-xs mx-auto">
                      You're all set. We'll notify you when something important happens.
                    </p>
                  </div>
                </div>
              </Transition>
            </div>

            {/* User Menu */}
            <Menu as="div" className="relative">
              {({ open }) => (
                <>
                  <Menu.Button className="flex items-center gap-2.5 px-2 sm:px-3 py-1.5 rounded-xl hover:bg-gradient-to-br hover:from-indigo-50 hover:to-blue-50 active:scale-95 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:ring-offset-1">
                    <div
                      className="h-9 w-9 flex items-center justify-center font-bold text-white rounded-xl flex-shrink-0 text-sm avatar-ring transition-transform duration-300 hover:scale-105"
                      style={{
                        background: brand.avatarGradient || "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                      }}
                    >
                      {username?.[0]?.toUpperCase() || "U"}
                    </div>

                    <span className="hidden lg:inline text-sm font-bold text-gray-700 max-w-[120px] truncate">
                      {username}
                    </span>

                    <ChevronDown
                      className={`hidden lg:block w-4 h-4 text-gray-400 transition-all duration-300 flex-shrink-0 ${
                        open ? "rotate-180 text-indigo-600" : ""
                      }`}
                      strokeWidth={2.5}
                    />
                  </Menu.Button>

                  <Transition
                    as={React.Fragment}
                    enter="transition ease-out duration-200"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-150"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                  >
                    <Menu.Items className="absolute right-0 mt-3 w-72 origin-top-right rounded-2xl bg-white shadow-2xl ring-1 ring-black ring-opacity-5 divide-y divide-gray-100 focus:outline-none overflow-hidden">
                      <div className="px-6 py-4 bg-gradient-to-br from-indigo-50 via-blue-50 to-indigo-50">
                        <p className="text-sm font-bold text-gray-900 truncate mb-1">{username}</p>
                        <p className="text-xs text-gray-600 font-semibold truncate flex items-center gap-1.5">
                          <Activity className="w-3 h-3" />
                          {source}
                        </p>
                      </div>

                      <div className="py-2">
                        <Menu.Item>
                          {({ active }) => (
                            <button
                              onClick={() => navigate("/change-password")}
                              className={`${
                                active ? "bg-gradient-to-r from-indigo-50 to-blue-50 text-indigo-700" : "text-gray-700"
                              } flex items-center gap-4 w-full px-6 py-3.5 text-sm font-semibold transition-all duration-200`}
                            >
                              <div className={`p-2 rounded-lg ${active ? 'bg-indigo-100' : 'bg-gray-100'}`}>
                                <Lock className="w-4 h-4 flex-shrink-0" strokeWidth={2} />
                              </div>
                              <span>Change Password</span>
                              <ArrowUpRight className="w-3.5 h-3.5 ml-auto opacity-50" />
                            </button>
                          )}
                        </Menu.Item>
                      </div>

                      <div className="py-2">
                        <Menu.Item>
                          {({ active }) => (
                            <button
                              onClick={handleLogout}
                              className={`${
                                active ? "bg-gradient-to-r from-red-50 to-rose-50 text-red-700" : "text-red-600"
                              } flex items-center gap-4 w-full px-6 py-3.5 text-sm font-bold transition-all duration-200`}
                            >
                              <div className={`p-2 rounded-lg ${active ? 'bg-red-100' : 'bg-red-50'}`}>
                                <LogOut className="w-4 h-4 flex-shrink-0" strokeWidth={2} />
                              </div>
                              <span>Logout</span>
                            </button>
                          )}
                        </Menu.Item>
                      </div>
                    </Menu.Items>
                  </Transition>
                </>
              )}
            </Menu>
          </div>

          {/* Marquees */}
          {visibleMarquees.length > 0 && (
            <div className="inside-marquee">
              <div
                style={{
                  display: "grid",
                  gridAutoRows: `${BAR_HEIGHT}px`,
                  rowGap: `${BAR_GAP}px`,
                }}
              >
                {visibleMarquees.map((m) => {
                  const vKey = m.variant || "info";
                  const v = MARQUEE_VARIANTS[vKey] || MARQUEE_VARIANTS.info;
                  const dir = m.direction === "ltr" ? "ltr" : "rtl";
                  const duration = Math.max(10, Math.min(20, Math.floor((m.text?.length || 40) / 3)));
                  return (
                    <div
                      key={m.id}
                      className="inside-marquee-item"
                      style={{
                        background: v.bg,
                        border: `1.5px solid ${v.border}`,
                        color: v.color,
                      }}
                      role="status"
                      aria-live="polite"
                    >
                      <div className="mr-2 opacity-90">{v.icon}</div>

                      <div
                        className="inside-marquee-track"
                        style={{
                          animation: `${dir === "ltr" ? "marquee-ltr" : "marquee-rtl"} ${duration}s linear infinite`,
                        }}
                      >
                        <span className="inside-marquee-text" style={{ color: v.color }}>
                          {m.text}
                        </span>
                        <span className="inside-marquee-text" style={{ color: v.color }} aria-hidden="true">
                          {m.text}
                        </span>
                        <span className="inside-marquee-text" style={{ color: v.color }} aria-hidden="true">
                          {m.text}
                        </span>
                      </div>

                      {m.cta && (
                        <button
                          type="button"
                          onClick={() => (m.cta.onClick ? m.cta.onClick() : m.cta.to ? navigate(m.cta.to) : null)}
                          className="inside-marquee-cta"
                          style={{
                            background: "rgba(255,255,255,.8)",
                            color: v.color,
                            border: `1px solid ${v.border}`,
                          }}
                        >
                          {m.cta.label}
                        </button>
                      )}

                      {(m.dismissible ?? true) && (
                        <button
                          type="button"
                          onClick={() => handleDismiss(m.id)}
                          className="inside-marquee-dismiss"
                          aria-label="Dismiss"
                          title="Dismiss"
                          style={{ color: v.color }}
                        >
                          <Close className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </header>
    </>
  );
}
